// frontend/src/features/ups/ManageAgencies.js
// Phase 8: UPS-admin Agency Management table
//
// Purpose:
// - Acts as the visual control plane for agencies
// - Makes contract state + site permissions visible at a glance
// - Entry point into deeper agency management through drawer + modals

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  fetchAgencies,
  deleteAgency,
  selectAllAgencies,
} from '../agencies/agenciesSlice';

import { loadMyCompanySites, selectCompanySites } from '../sites/sitesSlice';
import { fetchContracts, selectContractsState } from '../contracts/contractsSlice';

import { MoreHorizontal, Plus, Search, ChevronRight } from 'lucide-react';

import CreateAgencyWizardModal from './modals/CreateAgencyWizardModal';
import AddAccountModal from './modals/AddAccountModal';
import ConfirmDeleteModal from './modals/ConfirmDeleteModal';
import AgencyDrawer from './modals/AgencyDrawer';
import DriverMobileCard from './components/DriverMobileCard';
import { COMPANY_ACTION_CLASSES, CompanyStatusBadge } from './ui';

function RowActionsMenu({ onAdd, onDelete }) {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef(null);
  const firstItemRef = React.useRef(null);

  React.useEffect(() => {
    const onDocClick = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    const onKeydown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKeydown);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKeydown);
    };
  }, []);

  React.useEffect(() => {
    if (open) firstItemRef.current?.focus();
  }, [open]);

  return (
    <div ref={rootRef} className="relative inline-block text-left">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={COMPANY_ACTION_CLASSES.iconSecondary}
      >
        <MoreHorizontal size={18} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-2 w-48 overflow-hidden rounded-xl border border-app-border bg-app-card shadow-lg"
        >
          <button
            ref={firstItemRef}
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onAdd?.();
            }}
            className={COMPANY_ACTION_CLASSES.menuItem}
          >
            Add account
          </button>

          <button
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onDelete?.();
            }}
            className={COMPANY_ACTION_CLASSES.menuDestructive}
          >
            Delete agency
          </button>
        </div>
      )}
    </div>
  );
}

export default function ManageAgencies() {
  const dispatch = useDispatch();
  const searchInputId = 'manage-agencies-search';

  const agencies = useSelector(selectAllAgencies);
  const sites = useSelector(selectCompanySites);
  const contractsState = useSelector(selectContractsState);

  const loading = useSelector((s) => s.agencies.loading);
  const error = useSelector((s) => s.agencies.error);

  const [query, setQuery] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [currentAgency, setCurrentAgency] = useState(null);

  useEffect(() => {
    dispatch(fetchAgencies());
    dispatch(loadMyCompanySites());
    dispatch(fetchContracts());
  }, [dispatch]);

  const filtered = agencies.filter((a) =>
    a.name?.toLowerCase().includes(query.toLowerCase())
  );

  const contractByAgencyId = useMemo(() => {
    const m = new Map();
    const list = contractsState?.list || [];

    for (const c of list) {
      const agId = c.agency_id ?? c.agencyId;
      if (agId != null) m.set(Number(agId), c);
    }

    return m;
  }, [contractsState?.list]);

  const totalSites = Array.isArray(sites) ? sites.length : 0;

  const getContractBadge = (contract) => {
    if (!contract) return { label: 'No contract', tone: 'neutral' };

    if ((contract.status || '').toLowerCase() === 'active') {
      return { label: 'Active', tone: 'active' };
    }

    return { label: 'Disabled', tone: 'warning' };
  };

  const getAllowedSitesText = (contract) => {
    if (!contract) return '—';

    const status = (contract.status || '').toLowerCase();
    const isActive = status === 'active';

    const byContractId = contractsState?.sitesByContractId || {};
    const rows = byContractId[String(contract.id)] || byContractId[contract.id];

    if (Array.isArray(rows)) {
      const configured = rows.filter((r) => (r.status || '').toLowerCase() === 'active').length;

      const operational =
        typeof contract.operational_allowed_sites_count === 'number'
          ? contract.operational_allowed_sites_count
          : isActive
            ? configured
            : 0;

      return `${operational} / ${totalSites}`;
    }

    const configured =
      contract.configured_allowed_sites_count ??
      contract.allowed_sites_count ??
      contract.allowedSitesCount ??
      null;

    const operational =
      contract.operational_allowed_sites_count ??
      (typeof configured === 'number' ? (isActive ? configured : 0) : null);

    if (typeof operational === 'number') return `${operational} / ${totalSites}`;

    return `? / ${totalSites}`;
  };

  const openDrawer = (ag) => {
    setCurrentAgency(ag);
    setDrawerOpen(true);
  };

  const openAdd = (ag) => {
    setCurrentAgency(ag);
    setAddOpen(true);
  };

  const openDelete = (ag) => {
    setCurrentAgency(ag);
    setDeleteOpen(true);
  };

  return (
    <>
      <div className="space-y-6 p-6">
        {/* Page header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-app-accent">
              Administration
            </p>
            <h1 className="mt-2 text-2xl font-bold text-app-text">Manage Agencies</h1>
            <p className="mt-1 text-sm text-app-muted">
              Review agency contracts, site permissions, and account access.
            </p>
          </div>

          <button
            onClick={() => setCreateOpen(true)}
            className={`${COMPANY_ACTION_CLASSES.primary} gap-2`}
          >
            <Plus size={16} />
            New agency
          </button>
        </div>

        {/* Search and summary controls */}
        <section className="rounded-2xl border border-app-border bg-app-card p-5 shadow-sm">
          <div className="grid gap-4 lg:grid-cols-[1fr_auto] lg:items-end">
            <div>
              <label
                htmlFor={searchInputId}
                className="text-xs font-semibold uppercase tracking-[0.18em] text-app-muted"
              >
                Search
              </label>

              <div className="relative mt-2">
                <Search
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-app-muted"
                  size={16}
                />
                <input
                  id={searchInputId}
                  name="agencySearch"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search agencies…"
                  className="w-full rounded-xl border border-app-border bg-app-card py-3 pl-11 pr-4 text-sm text-app-text outline-none transition placeholder:text-app-muted focus:border-app-primary focus:ring-2 focus:ring-app-primary/15"
                />
              </div>
            </div>

            <div className="rounded-full bg-app-surface px-4 py-2 text-sm font-semibold text-app-muted">
              {filtered.length} shown
            </div>
          </div>

          {error && (
            <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-700">
              {error}
            </div>
          )}
        </section>

        {/* Agencies table */}
        <section className="overflow-hidden rounded-2xl border border-app-border bg-app-card shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-app-border px-6 py-5">
            <div>
              <h2 className="text-xl font-bold text-app-text">Agencies</h2>
              <p className="mt-1 text-sm text-app-muted">
                Open an agency to manage contracts, allowed sites, and users.
              </p>
            </div>

            <div className="rounded-full bg-app-surface px-4 py-2 text-sm font-semibold text-app-muted">
              {agencies.length} total
            </div>
          </div>

          <div className="hidden overflow-x-auto xl:block">
            <table className="min-w-full bg-app-card text-sm">
              <thead className="border-b border-app-border bg-app-surface">
                <tr>
                  {['ID', 'Name', 'Contract', 'Allowed Sites', 'Actions'].map((col) => (
                    <th
                      key={col}
                      className={`px-6 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-app-muted ${
                        col === 'Actions' ? 'text-right' : 'text-left'
                      }`}
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody className="divide-y divide-app-border">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-sm text-app-muted">
                      Loading agencies…
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-sm text-app-muted">
                      {query.trim()
                        ? `No agencies match “${query.trim()}”.`
                        : 'No agencies found.'}
                    </td>
                  </tr>
                ) : (
                  filtered.map((ag) => {
                    const contract = contractByAgencyId.get(Number(ag.id));
                    const badge = getContractBadge(contract);
                    const allowedText = getAllowedSitesText(contract);

                    return (
                      <tr key={ag.id} className="transition hover:bg-app-surface">
                        <td className="whitespace-nowrap px-6 py-5 text-sm text-app-muted">
                          {ag.id}
                        </td>

                        <td className="px-6 py-5">
                          <button
                            onClick={() => openDrawer(ag)}
                            className="inline-flex items-center gap-2 text-left font-semibold text-app-text transition hover:text-app-primary"
                          >
                            {ag.name || '—'}
                            <ChevronRight size={16} />
                          </button>
                        </td>

                        <td className="whitespace-nowrap px-6 py-5">
                          <CompanyStatusBadge tone={badge.tone}>{badge.label}</CompanyStatusBadge>
                        </td>

                        <td className="whitespace-nowrap px-6 py-5 text-sm font-medium text-app-text">
                          {allowedText}
                        </td>

                        <td className="whitespace-nowrap px-6 py-5 text-right">
                          <RowActionsMenu
                            onAdd={() => openAdd(ag)}
                            onDelete={() => openDelete(ag)}
                          />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="space-y-3 p-4 xl:hidden">
            {loading ? (
              <div className="rounded-2xl border border-app-border bg-app-surface px-4 py-10 text-center text-sm text-app-muted">
                Loading agencies...
              </div>
            ) : filtered.length === 0 ? (
              <div className="rounded-2xl border border-app-border bg-app-surface px-4 py-10 text-center text-sm text-app-muted">
                {query.trim()
                  ? `No agencies match "${query.trim()}".`
                  : 'No agencies found.'}
              </div>
            ) : (
              filtered.map((ag) => {
                const contract = contractByAgencyId.get(Number(ag.id));
                const badge = getContractBadge(contract);
                const allowedText = getAllowedSitesText(contract);
                const avatarText = (ag.name || '')
                  .split(' ')
                  .map((part) => part[0])
                  .join('')
                  .slice(0, 2);

                return (
                  <DriverMobileCard
                    key={ag.id}
                    tone="request"
                    title={ag.name || '-'}
                    subtitle={`Agency ID - ${ag.id || '-'}`}
                    avatarText={avatarText}
                    badges={[
                      <CompanyStatusBadge key="contract" tone={badge.tone}>
                        {badge.label}
                      </CompanyStatusBadge>,
                    ]}
                    fields={[
                      ['Allowed Sites', allowedText],
                    ]}
                    actions={
                      <>
                        <button
                          onClick={() => openDrawer(ag)}
                          className={COMPANY_ACTION_CLASSES.compactSecondary}
                        >
                          Manage
                        </button>

                        <RowActionsMenu
                          onAdd={() => openAdd(ag)}
                          onDelete={() => openDelete(ag)}
                        />
                      </>
                    }
                  />
                );
              })
            )}
          </div>
        </section>
      </div>

      {/* Modals and drawers */}
      {createOpen && (
        <CreateAgencyWizardModal
          isOpen={createOpen}
          onClose={() => setCreateOpen(false)}
        />
      )}

      {addOpen && currentAgency && (
        <AddAccountModal
          isOpen={addOpen}
          agency={currentAgency}
          onClose={() => {
            setAddOpen(false);
            setCurrentAgency(null);
          }}
        />
      )}

      {deleteOpen && currentAgency && (
        <ConfirmDeleteModal
          isOpen={deleteOpen}
          agency={currentAgency}
          onConfirm={async () => {
            await dispatch(deleteAgency(currentAgency.id));
            setDeleteOpen(false);
            setCurrentAgency(null);
          }}
          onClose={() => {
            setDeleteOpen(false);
            setCurrentAgency(null);
          }}
        />
      )}

      {drawerOpen && currentAgency && (
        <AgencyDrawer
          isOpen={drawerOpen}
          agency={currentAgency}
          onClose={() => setDrawerOpen(false)}
          onAddAccount={() => {
            setDrawerOpen(false);
            setAddOpen(true);
          }}
        />
      )}
    </>
  );
}
